import { useCallback, useEffect, useState } from "react";
import type { Company, View } from "./types.ts";
import { createCompany, listCompanies, renameCompany } from "./api.ts";
import { CompanyApp } from "./CompanyApp.tsx";

const COMPANY_KEY = "likvid_company";

function readStoredCompanyId(): string | null {
  try {
    return localStorage.getItem(COMPANY_KEY);
  } catch {
    return null;
  }
}

function storeCompanyId(id: string): void {
  try {
    localStorage.setItem(COMPANY_KEY, id);
  } catch {
    return;
  }
}

type Route = { companyId: string | null; view: View };

const COMPANY_ROUTE = /^#\/c\/([^/]+)(\/kostnader)?\/?$/;

function safeDecode(s: string): string | null {
  try {
    return decodeURIComponent(s);
  } catch {
    return null;
  }
}

export function parseHash(hash: string): Route {
  const m = COMPANY_ROUTE.exec(hash);
  if (m) {
    return {
      companyId: safeDecode(m[1]!),
      view: m[2] ? "kostnader" : "likviditet",
    };
  }
  return {
    companyId: null,
    view: hash === "#/kostnader" ? "kostnader" : "likviditet",
  };
}

export function routeHash(companyId: string, view: View): string {
  const base = `#/c/${encodeURIComponent(companyId)}`;
  return view === "kostnader" ? `${base}/kostnader` : base;
}

function resolveCompanyId(
  companies: Company[],
  requested: string | null,
): string | null {
  const known = (id: string | null) =>
    id !== null && companies.some((c) => c.id === id);
  if (known(requested)) return requested;
  const stored = readStoredCompanyId();
  if (known(stored)) return stored;
  return companies[0]?.id ?? null;
}

export function App() {
  const [companies, setCompanies] = useState<Company[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [route, setRoute] = useState<Route>(() => parseHash(location.hash));

  const loadCompanies = useCallback(async (signal?: { cancelled: boolean }) => {
    setLoadFailed(false);
    try {
      const list = await listCompanies();
      if (!signal?.cancelled) setCompanies(list);
    } catch {
      if (!signal?.cancelled) setLoadFailed(true);
    }
  }, []);

  useEffect(() => {
    const signal = { cancelled: false };
    void loadCompanies(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [loadCompanies]);

  useEffect(() => {
    const onChange = () => setRoute(parseHash(location.hash));
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);

  const companyId = companies
    ? resolveCompanyId(companies, route.companyId)
    : null;

  useEffect(() => {
    if (!companyId) return;
    storeCompanyId(companyId);
    const hash = routeHash(companyId, route.view);
    if (location.hash !== hash) {
      history.replaceState(null, "", hash);
      setRoute({ companyId, view: route.view });
    }
  }, [companyId, route.view]);

  const navigate = useCallback((id: string, view: View) => {
    location.hash = routeHash(id, view);
  }, []);

  const changeView = useCallback(
    (view: View) => {
      if (companyId) navigate(companyId, view);
    },
    [companyId, navigate],
  );

  const switchCompany = useCallback(
    (id: string) => navigate(id, route.view),
    [navigate, route.view],
  );

  const addCompany = useCallback(async (name: string) => {
    const company = await createCompany(name);
    setCompanies((list) => [...(list ?? []), company]);
    return company;
  }, []);

  const changeCompanyName = useCallback(async (id: string, name: string) => {
    const company = await renameCompany(id, name);
    setCompanies((list) =>
      (list ?? []).map((c) => (c.id === id ? company : c)),
    );
  }, []);

  if (loadFailed) {
    return (
      <div className="load-error" role="alert">
        <p>Kunde inte hämta företagen.</p>
        <button type="button" onClick={() => void loadCompanies()}>
          Försök igen
        </button>
      </div>
    );
  }

  if (!companies || !companyId) return null;

  return (
    <CompanyApp
      key={companyId}
      companyId={companyId}
      companies={companies}
      view={route.view}
      onViewChange={changeView}
      onSwitchCompany={switchCompany}
      onCreateCompany={addCompany}
      onRenameCompany={changeCompanyName}
    />
  );
}
